import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaLayerGroup, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useVehicle } from '../../contexts/VehicleContext';
import { bookingService, publicGarageService } from '../../services/apiService';

function toLocalDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function nextDays(count = 7) {
  const out = [];
  const today = new Date();
  for (let i = 0; i < count; i += 1) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    out.push(toLocalDateStr(d));
  }
  return out;
}

export default function SplitGarageBooking() {
  const navigate = useNavigate();
  const location = useLocation();
  const { vehicles, loading: vehiclesLoading } = useVehicle();
  const split = location.state?.split;

  const [vehicleId, setVehicleId] = useState('');
  const [slotDate, setSlotDate] = useState(nextDays(1)[0]);
  const [slotTime, setSlotTime] = useState('10:00');
  const [problemDescription, setProblemDescription] = useState('');
  const [pickupDrop, setPickupDrop] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [slotOptions, setSlotOptions] = useState(['09:00', '10:00', '11:00', '14:00', '16:00']);

  const vehicleList = useMemo(
    () => (Array.isArray(vehicles) ? vehicles : []),
    [vehicles]
  );

  const legs = Array.isArray(split?.legs) ? split.legs : [];

  useEffect(() => {
    if (!vehicleList.length) return;
    if (!vehicleId) {
      const def = vehicleList.find((v) => v.isDefault) || vehicleList[0];
      setVehicleId(String(def._id || def.id || ''));
    }
  }, [vehicleList, vehicleId]);

  useEffect(() => {
    if (!legs.length) return;
    const labels = legs.map((l) => l.label).join(' + ');
    setProblemDescription((prev) => prev || `Split booking for: ${labels}. Please diagnose and coordinate.`);
  }, [legs]);

  useEffect(() => {
    let cancelled = false;
    async function loadSlots() {
      const firstGarageId = legs[0]?.garage?.id;
      if (!firstGarageId || !slotDate) return;
      try {
        const data = await publicGarageService.getSlots(firstGarageId, slotDate);
        const times = (data?.slots || [])
          .filter((s) => s?.available !== false)
          .map((s) => s.time || s.slotTime)
          .filter(Boolean);
        if (!cancelled && times.length) {
          setSlotOptions(times);
          if (!times.includes(slotTime)) setSlotTime(times[0]);
        }
      } catch {
        // keep defaults
      }
    }
    loadSlots();
    return () => {
      cancelled = true;
    };
  }, [legs, slotDate]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!split || legs.length < 2) {
    return (
      <div className="mx-auto max-w-lg px-4 py-12 text-center">
        <p className="text-gray-700">No split booking selected.</p>
        <Link to="/garages" className="mt-4 inline-block text-sm font-semibold text-blue-600 hover:underline">
          Back to garage search
        </Link>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!vehicleId) {
      toast.error('Select a vehicle');
      return;
    }
    if (!problemDescription || problemDescription.trim().length < 10) {
      toast.error('Describe the issue (at least 10 characters)');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        vehicleId,
        slotDate,
        slotTime,
        problemDescription: problemDescription.trim(),
        pickupDrop,
        legs: legs.map((leg) => ({
          garageId: leg.garage?.id,
          serviceType: leg.serviceType || leg.label,
          label: leg.label,
          clusterId: leg.clusterId,
          slotDate,
          slotTime,
          problemDescription: `${leg.label}: ${problemDescription.trim()}`,
          pickupDrop,
        })),
      };
      const result = await bookingService.createSplit(payload);
      toast.success('Split bookings confirmed — each garage received a job card.');
      const firstBookingId = result?.bookingIds?.[0] || result?.bookings?.[0]?.bookingId;
      if (firstBookingId) {
        navigate(`/service/booking/status/${firstBookingId}`, {
          state: { splitGroupId: result.splitGroupId, bookings: result.bookings },
        });
      } else {
        navigate('/service/jobs');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to create split bookings');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-6 sm:px-6">
      <Link to="/garages" className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline">
        <FaChevronLeft className="text-xs" /> Back to search
      </Link>

      <div className="mt-4 flex items-center gap-2">
        <FaLayerGroup className="text-indigo-600" />
        <h1 className="text-xl font-bold text-gray-900">Split service booking</h1>
      </div>
      <p className="mt-1 text-sm text-gray-600">
        One coordinated booking creates a separate job card for each garage. Pickup coordination uses your shared notes.
      </p>

      <ul className="mt-4 space-y-2 rounded-xl border border-gray-200 bg-white p-4">
        {legs.map((leg) => (
          <li key={`${leg.clusterId}-${leg.garage?.id}`} className="text-sm text-gray-800">
            <span className="font-semibold">{leg.label}</span>
            <span className="text-gray-500"> → {leg.garage?.name || 'Garage'}</span>
          </li>
        ))}
      </ul>

      <form onSubmit={handleSubmit} className="mt-5 space-y-4 rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Vehicle *</label>
          {vehiclesLoading ? (
            <p className="text-sm text-gray-500">Loading vehicles…</p>
          ) : (
            <select
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
              required
            >
              <option value="">Select vehicle</option>
              {vehicleList.map((v) => (
                <option key={v._id || v.id} value={v._id || v.id}>
                  {v.make} {v.model} ({v.registrationNumber || '—'})
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Preferred date *</label>
            <select
              value={slotDate}
              onChange={(e) => setSlotDate(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
            >
              {nextDays(7).map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Preferred time *</label>
            <select
              value={slotTime}
              onChange={(e) => setSlotTime(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
            >
              {slotOptions.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Issue details *</label>
          <textarea
            value={problemDescription}
            onChange={(e) => setProblemDescription(e.target.value)}
            rows={4}
            className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
            placeholder="Describe symptoms for both services…"
            required
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={pickupDrop}
            onChange={(e) => setPickupDrop(e.target.checked)}
          />
          Request pickup &amp; drop coordination across both garages
        </label>

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
        >
          {submitting ? <FaSpinner className="animate-spin" /> : null}
          Confirm split booking
        </button>
      </form>
    </div>
  );
}
